import * as angular from "angular";
import {IQService} from "angular";
import * as _ from "lodash";
import {PlanningAlertsService} from "./planning-alerts.service";
import {PlanningRequestsService} from "./planning-requests.service";
import {BringozLoaderService} from "../../commons/services/bringoz-loader.service";
import {RouteSwapResourceRequestModel} from "../models/route-swap-resource-request.model";
import {
  PlanningDraftEditRouteNameRequestModel
} from "../models/planning-draft-edit-route-name-request.model";
import {SplitService} from "../../commons/services/split-service.service";
import {PlanningDraftEditRouteColorRequestModel} from "../models/planning-draft-edit-route-color-request.model";
import {PlanningDraftRouteFeedbackRequestModel} from "../models/planning-draft-route-feedback-request.model";
import {PlanningClusteringAreasRequestsService} from "./planning-clustering-areas-requests.service";
import {ClusteringMode} from "../types/clustering-mode.type";


export class PlanningDraftsService {


  constructor(private planningRequestsService: PlanningRequestsService, private planningAlertsService: PlanningAlertsService,
              private $q: IQService, private bringozLoaderService: BringozLoaderService,
              private $log, private splitService: SplitService,
              private planningClusteringAreasRequestsService: PlanningClusteringAreasRequestsService) {

  }

  public getDrafts(draftIds, projectId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getDraftsInProject(draftIds, projectId)
      .then((draft) => {
        deferred.resolve(draft)
      })
      .catch((error) => {
        this.$log.error(error);
        // PlanningAlerts.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }


  public getDraft(draftId, projectId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getDraftInProject(draftId, projectId)
      .then((draft) => {
        deferred.resolve(draft)
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise;
  }

  public getDraftAnalysis(draftId) {
    return this.getAnalysis(draftId);
  }

  public getDraftWithAnalysis(draftId, projectId) {
    const draft = this.getDraft(draftId, projectId),
      analysis = this.getAnalysis(draftId);

    return this.$q.all([draft, analysis]);
  }

  public createDraft(settings, draftName: string, excludeJobIds, excludeResourceIds, projectId, isOptimizedDraft, draftTemplateId?: string, useDraftTemplateSettings?: boolean, optimizationSteps?: any[]) {
    const deferred = this.$q.defer();

    const supportLayeredPlanning = this.splitService.getSupportLayeredPlanning();

    this.setLoading(true);
    this.planningRequestsService.createDraftInProject(settings, draftName, excludeJobIds, excludeResourceIds, projectId, isOptimizedDraft, draftTemplateId, useDraftTemplateSettings, supportLayeredPlanning, optimizationSteps)
      .then((createdDraft) => {
        // PlanningAlerts.createDraftInProjectSuccess();
        deferred.resolve(createdDraft);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public createEmptyDraft(settings, draftName: string, excludeJobIds, excludeResourceIds, projectId, isOptimizedDraft, draftTemplateId?: string, useDraftTemplateSettings?: boolean, optimizationSteps?: any[]) {
    const deferred = this.$q.defer();

    const supportLayeredPlanning = this.splitService.getSupportLayeredPlanning();

    this.setLoading(true);
    this.planningRequestsService.createDraftInProject(settings, draftName, excludeJobIds, excludeResourceIds, projectId, isOptimizedDraft, draftTemplateId, useDraftTemplateSettings, supportLayeredPlanning, optimizationSteps)
      .then((createdDraft) => {
        // PlanningAlerts.createDraftInProjectSuccess();
        deferred.resolve(createdDraft);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateDraft(draft, draftId, project) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateDraft(draft, draftId, project.id)
      .then((updatedSettings) => {
        this.planningAlertsService.updateProjectSettingsSuccess();
        deferred.resolve(updatedSettings);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.updateProjectSettingsError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateAddress(projectId: string,
                       draftId: string, id: string, address: any, updateServices?: boolean, updateAccounts?: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateAddress(projectId, draftId,
      id, address, updateServices, updateAccounts)
      .then((updatedSettings) => {
        this.planningAlertsService.updateProjectSettingsSuccess();
        deferred.resolve(updatedSettings);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.updateProjectSettingsError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public duplicateDraft(draftId, projectId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.duplicateDraft(draftId, projectId)
      .then((duplicatedDraft) => {
        deferred.resolve(duplicatedDraft);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateDraftSettings(settings, draftId, project) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateDraftSettings(settings, draftId, project.id)
      .then((updatedSettings) => {
        this.planningAlertsService.updateProjectSettingsSuccess();
        deferred.resolve(updatedSettings);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.updateProjectSettingsError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }


  public optimizeDraft(draftId, project) {
    const deferred = this.$q.defer();

    const supportLayeredPlanning = this.splitService.getSupportLayeredPlanning();
    // _setLoading(true);
    this.planningRequestsService.optimizeDraft(draftId, project.id, supportLayeredPlanning)
      .then((reOptimizedDraft) => {
        // PlanningAlerts.optimizingDraftInfo({id: draftId});
        deferred.resolve(reOptimizedDraft);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.optimizingDraftError();
        deferred.reject(error)
      })
      .finally(() => {
        // _setLoading(false);
      });

    return deferred.promise
  }

  public abortOptimizeDraft(draftId, projectId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.abortOptimizeDraft(draftId, projectId)
      .then(() => {
        this.planningAlertsService.abortOptimizingDraftSuccess();
        deferred.resolve();
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.abortOptimizingDraftError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public deleteDraftsFromProject(draftIds, project) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.deleteDraftsFromProject(draftIds, project.id)
      .then((deletedDraftIds) => {
        this.planningAlertsService.deleteDraftsFromProjectSuccess();
        deferred.resolve(deletedDraftIds);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.deleteDraftsFromProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  approveAndDispatchDraft(draftId, project, dispatchLeftovers, autoAssignRoutes, keepProjectOpen) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.approveAndDispatchDraft(draftId, project.id, dispatchLeftovers, autoAssignRoutes, keepProjectOpen)
      .then((result) => {
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);

        if (error.status === 413106) {
          this.planningAlertsService.draftCannotBeDispatchedError();
        } else {
          this.planningAlertsService.approveAndDispatchDraftError();
        }
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  approveAndDispatchRoutes(draftId, projectId, routeIds) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.approveAndDispatchRoutes(draftId, projectId, routeIds)
      .then((result) => {
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);

        if (error.status === 413106) {
          this.planningAlertsService.draftCannotBeDispatchedError();
        } else {
          this.planningAlertsService.approveAndDispatchDraftError();
        }
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public getJobs(projectId, draftId, jobIds) {
    const deferred = this.$q.defer();

    this.planningRequestsService.getDraftJobs(projectId, draftId, jobIds)
      .then((jobs) => {
        deferred.resolve(jobs)
      })
      .catch((error) => {
        this.planningAlertsService.addJobToProjectError();
        this.$log.error(error);
        deferred.reject(error)
      });

    return deferred.promise
  }

  public getResources(projectId, draftId, filterCanCreateRoute?: boolean) {
    const deferred = this.$q.defer();

    this.planningRequestsService.getDraftResources(projectId, draftId, filterCanCreateRoute)
      .then((resources) => {
        deferred.resolve(resources)
      })
      .catch((error) => {
        // PlanningAlerts.addResourcesToProjectSuccess();
        this.$log.error(error);
        deferred.reject(error)
      });

    return deferred.promise
  }

  public getAccountsMetrics(projectId, draftId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getDraftAccountsMetrics(projectId, draftId)
      .then((accountsData) => {
        deferred.resolve(accountsData)
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public getSolutionMetrics(projectId, draftId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getDraftSolutionMetrics(projectId, draftId)
      .then((solutionData) => {
        deferred.resolve(solutionData)
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  createDraftResources(projectId, draftId, payload) {
    const deferred = this.$q.defer();

    this.planningRequestsService.createDraftResources(projectId, draftId, payload)
      .then((resources) => {
        deferred.resolve(resources)
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      });

    return deferred.promise
  }

  public getRoutes(projectId, draftId, routeIds, groupRoutes) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.getDraftRoutes(projectId, draftId, routeIds, groupRoutes)
      .then((routes) => {
        deferred.resolve(routes)
      })
      .catch((error) => {
        // PlanningAlerts.addRoutesToProjectSuccess();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateDraftJobs(projectId, draftId, includeIds, excludeIds) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateDraftJobs(projectId, draftId, includeIds, excludeIds)
      .then((removedJobs) => {
        this.planningAlertsService.updateJobInDraftSuccess();
        deferred.resolve(removedJobs)
      })
      .catch((error) => {
        this.planningAlertsService.updateJobInDraftError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });


    return deferred.promise
  }

  public updateDraftResources(projectId, draftId, includeIds, excludeIds) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateDraftResources(projectId, draftId, includeIds, excludeIds)
      .then((removedResources) => {
        this.planningAlertsService.updateResourcesInDraftSuccess();
        deferred.resolve(removedResources)
      })
      .catch((error) => {
        this.planningAlertsService.updateResourcesInDraftError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateResourceSequence(projectId, draftId, routeGroupId: string, orderedRouteIds: string[]) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateResourceSequence(projectId, draftId, routeGroupId, orderedRouteIds)
      .then((updatedResource) => {
        this.planningAlertsService.updateResourcesInDraftSuccess();
        deferred.resolve(updatedResource)
      })
      .catch((error) => {
        this.planningAlertsService.updateResourcesInDraftError();
        this.$log.error('Failed to update resource:', error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateDraftResource(projectId, draftId, resourceId, payload) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateDraftResource(projectId, draftId, resourceId, payload)
      .then((updatedResource) => {
        this.planningAlertsService.updateResourcesInDraftSuccess();
        deferred.resolve(updatedResource)
      })
      .catch((error) => {
        this.planningAlertsService.updateResourcesInDraftError();
        this.$log.error('Failed to update resource:', error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public addStopsToRoute(routeId, jobIds, order, doOptimization, project, draftId, allowSkillsMismatch?: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.addStopsToRoute(routeId, jobIds, order, doOptimization, project.id, draftId, allowSkillsMismatch)
      .then((result) => {
        this.planningAlertsService.updateRouteSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.updateRouteError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public recalcRoutesInDraft(project, draftId: string, routeIds: string[], ignoreSequence?: boolean, preserveJobs?: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.recalcRoutesInDraft(project.id, draftId, routeIds, ignoreSequence, preserveJobs)
      .then((result) => {
        this.planningAlertsService.recalcRouteSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.recalcRouteError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public deleteRoutesInDraft(routeIds, projectId, draftId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.deleteRoutesInDraft(routeIds, projectId, draftId)
      .then((result) => {
        this.planningAlertsService.deleteRouteSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.deleteRouteError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateRouteLockState(projectId: string, draftId: string, routeId: string, isBlockedByUser: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRouteLockState(projectId, draftId, routeId, isBlockedByUser)
      .then((result) => {
        if (isBlockedByUser) {
          this.planningAlertsService.lockRouteSuccess();
        } else {
          this.planningAlertsService.unlockRouteSuccess();
        }
        deferred.resolve(result);
      })
      .catch((error) => {
        if (isBlockedByUser) {
          this.planningAlertsService.lockRouteError();
        } else {
          this.planningAlertsService.unlockRouteError();
        }
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public lockJobs(projectId: string, draftId: string, jobIdsByRouteId: Record<string, string[]>) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.lockJobs(projectId, draftId, jobIdsByRouteId)
      .then((result) => {
        this.planningAlertsService.lockJobSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.planningAlertsService.lockJobError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public unlockJobs(projectId: string, draftId: string, jobIds: string[]) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.unlockJobs(projectId, draftId, jobIds)
      .then((result) => {
        this.planningAlertsService.unlockJobSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.planningAlertsService.unlockJobError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }


  public moveActivityInRoutes(payload?, project?, draftId?, groupRoutes?, allowSkillsMismatch?: boolean) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.moveActivityInRoutes(payload, project.id, draftId, groupRoutes, allowSkillsMismatch)
      .then((result) => {
        this.planningAlertsService.actionReceivedSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.actionReceivedError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public addJobsToPlanningRoute(payload?, project?, draftId?, groupRoutes?) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.addJobsToPlanningRoute(payload, project.id, draftId, groupRoutes)
      .then((result) => {
        this.planningAlertsService.actionReceivedSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        switch (error.status) {
          case 400463:
            this.planningAlertsService.routeIndexOutOfBoundsError();
            break;
          case 400464:
            this.planningAlertsService.routeIndexOrderInvalidError();
            break;
          case 400465:
            this.planningAlertsService.jobAlreadyOnRouteError();
            break;
          case 400466:
            this.planningAlertsService.noMatchingPickupLocationError();
            break;
          case 400467:
            this.planningAlertsService.noMatchingDropoffLocationError();
            break;
          default:
            this.planningAlertsService.actionReceivedError();
        }
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise;
  }

  public getDraftRouteMockData(payload, projectId, draftId) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.getDraftRouteMockData(payload, projectId, draftId)
      .then((result) => {
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public assignAgent(projectId, draftId, routeIds: string[], agentId: string) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.assignAgent(projectId, draftId, routeIds, agentId)
      .then((result) => {
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public createRouteInDraft(resourceId, jobIds, projectId, draftId, groupRoutes, routeName?, stopIdOrder?, optimize?, allowSkillsMismatch?: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.createRouteInDraft(resourceId, jobIds, draftId, projectId, groupRoutes, routeName, stopIdOrder, optimize, allowSkillsMismatch)
      .then((result) => {
        this.planningAlertsService.createRouteInDraftSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createRouteInDraftError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  // public getRoutesBySyncIds(syncIds) {
  //   const deferred = this.$q.defer();
  //
  //   this.setLoading(true);
  //   this.routeRequestsService.createProjectWithOrders(syncIds)
  //     .then((result) => {
  //       this.planningAlertsService.createProjectSuccess();
  //       deferred.resolve(result);
  //     })
  //     .catch((error) => {
  //       this.$log.error(error);
  //       this.planningAlertsService.createProjectError();
  //       deferred.reject(error)
  //     })
  //     .finally(() => {
  //       this.setLoading(false);
  //     });
  //
  //   return deferred.promise
  // }

  public exportDraft(projectId, draftId, exportType, routeIds?, email?) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.exportDraft(projectId, draftId, exportType, routeIds, email)
      .then((result) => {
        this.planningAlertsService.exportDraftSuccess();
        deferred.resolve(result);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.exportDraftError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public exportDraftReport(projectId, draftId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.exportDraftReport(projectId, draftId)
      .then((res) => {
        this.planningAlertsService.exportDraftSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.exportDraftError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  // ******************************
  //        Private methods
  // ******************************

  private setLoading(isActive) {
    isActive ? this.bringozLoaderService.on() : this.bringozLoaderService.off();
  }


  private getAnalysis(draftId) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getDraftAnalysisInProject(draftId)
      .then((analysis) => {
        deferred.resolve(analysis)
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createDraftInProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise;
  }

  public swapRouteResources(projectId: string, draftId: string, payload: RouteSwapResourceRequestModel[]) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.swapRouteResources(projectId, draftId, payload)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public changeRouteResource(projectId: string, draftId: string, routeId: string, destinationResourceId: string) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.changeRouteResource(projectId, draftId, routeId, destinationResourceId)
      .then((res) => {
        this.planningAlertsService.changeRouteResourceSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.changeRouteResourceError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateRoutePickingTimeOffset(projectId: string, draftId: string, routeId: string, pickingTimeOffsetInMinutes: number) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRoutePickingTimeOffset(projectId, draftId, routeId, pickingTimeOffsetInMinutes)
      .then((res) => {
        this.planningAlertsService.updateRouteSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.updateRouteError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateRoutesPickingTime(projectId: string, draftId: string, routeIds: string[], pickingTime: number) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRoutesPickingTime(projectId, draftId, routeIds, pickingTime)
      .then((res) => {
        if (routeIds?.length === 1) {
          this.planningAlertsService.updateRouteSuccess();
        } else {
          this.planningAlertsService.updateRoutesSuccess();
        }
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        if (routeIds?.length === 1) {
          this.planningAlertsService.updateRouteError();
        } else {
          this.planningAlertsService.updateRoutesError();
        }
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }


  public updateRouteName(projectId: string, draftId: string, routeId: string,
                         payload: PlanningDraftEditRouteNameRequestModel) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRouteName(projectId, draftId, routeId, payload)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateRouteColor(projectId: string, draftId: string, routeId: string,
                         payload: PlanningDraftEditRouteColorRequestModel) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRouteColor(projectId, draftId, routeId, payload)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public updateRouteFeedback(projectId: string, draftId: string, routeId: string,
                             payload: PlanningDraftRouteFeedbackRequestModel) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.updateRouteFeedback(projectId, draftId, routeId, payload)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error);
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise;
  }

  public splitJobByPackages(projectId: string, draftId: string, draftJobId: string, request: {splits: [{packages: any[], resourceId?: number }]}) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.splitByPackages(projectId, draftId, draftJobId, request)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public splitAndAssignJobByPackages(projectId: string, draftId: string, draftJobId: string, request: {splits: [{packages: any[], resourceId?: number, routeId?: number }]}, optimize: boolean) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.splitAndAssignJobByPackages(projectId, draftId, draftJobId, request, optimize)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public splitJobByLocation(projectId: string, draftId: string, draftJobId: string, request: {stops: {address: {locationId: string, locationType: string}}[]}) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.splitJobByLocation(projectId, draftId, draftJobId, request)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public getJobSplitSuggestions(projectId: string, draftId: string, suggestionId: string) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.getJobSplitSuggestions(projectId, draftId, suggestionId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.createProjectError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public reverseJobSplit(projectId: string, draftId: string, splitSuggestionId: string) {
    const deferred = this.$q.defer();

    this.setLoading(true);
    this.planningRequestsService.reverseJobSplit(projectId, draftId, splitSuggestionId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        if (!this.planningAlertsService.handleJobSplitUndoError(error)) {
          this.planningAlertsService.createProjectError();
        }
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise
  }

  public generateJobSplitSuggestions(projectId: string, draftId: string) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.generateJobSplitSuggestions(projectId, draftId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.generateJobSplitSuggestionsError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise

  }

  public bulkJobSplit(projectId: string, draftId: string, payload?: any) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.bulkJobSplit(projectId, draftId, payload)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.planningAlertsService.bulkJobSplitFailed();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise

  }

  public generateDraftRecommendation(projectId: string, draftId: string, recommendationConfigurationId: string) {
    const deferred = this.$q.defer();
    this.setLoading(true);
    this.planningRequestsService.generateRecommendations(projectId, draftId, recommendationConfigurationId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        // this.planningAlertsService.generateRecommendationError();
        deferred.reject(error)
      })
      .finally(() => {
        this.setLoading(false);
      });

    return deferred.promise

  }
}

angular
  .module('bringoz.admin.planning')
  .service('planningDraftsService', PlanningDraftsService);
